import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { App, Button, Form, Input, Modal, Space } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { EditSubscriberModal } from './EditSubscriberModal'
import { GRANT_STALE_MS } from './SubscriberGrants'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  activateSubscriber,
  blockSubscriber,
  changePassword,
  closeSubscriber,
  fetchGrantOverview,
  resumeSubscriber,
  suspendSubscriber,
  unblockSubscriber,
  type Subscriber,
} from './subscriberApi'

interface SubscriberActionsProps {
  subscriber: Subscriber
}

type ReasonAction = 'block' | 'suspend' | 'close'

/**
 * Abonent holatini boshqarish va maydonlarini tahrirlash.
 *
 * ⚠️ TAHRIRLASH shu yerda, holat amallari bilan BIR QATORDA turadi va bu
 * ataylab: operator kartochkani ochganda "nima qila olaman" degan savolga
 * bitta joydan javob topishi kerak. Uni alohida joyga chiqarish (masalan
 * "Asosiy ma'lumot" kartasi ichiga) tugmalarni ikkiga bo'lardi va
 * tahrirlash imkoniyati ko'rinmay qolardi — aynan shu sabab u uzoq vaqt
 * "yo'q" deb hisoblangan edi.
 *
 * TUGMALAR HOLATGA QARAB CHIZILADI, hammasi bir vaqtda emas. Sabab
 * `SubscriberState` da yozilgan holat mashinasi: `BLOCKED` abonentga
 * "to'xtatish" tugmasini ko'rsatish backend rad javobi bilan tugardi
 * (`canTransitionTo` false), operator esa nima uchunligini bilmasdi.
 *
 * ⚠️ `SUSPENDED` va `BLOCKED` ATAYLAB alohida:
 *   · `resume`  — TIZIM to'xtatganini ochadi (mablag' tugagan edi)
 *   · `unblock` — ODAM to'xtatganini ochadi
 * Ularni bitta tugmaga birlashtirish "nega o'zi ochilib ketdi?" degan
 * javobsiz savolga olib kelardi.
 */
export function SubscriberActions({ subscriber }: SubscriberActionsProps) {
  const { t } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  // Turg'un oynalar React daraxti ICHIDA — ularga bu tegishli emas.
  const { modal } = App.useApp()
  const [passwordOpen, setPasswordOpen] = useState(false)

  /*
    ⚠️ AMALDAGI GRANT BORMI — faollashtirishdan OLDIN bilish kerak.

    Jonli tizimda shu ketma-ketlik yozib olindi:

      16:57:43  balans to'ldirildi (31 AFN)
      16:57:58  abonent QO'LDA faollashtirildi
      16:58:48  enforcement: "grantlar: grant yo'q"
      16:58:53  abonent QAYTA TO'XTATILDI

    Operator "Faollashtirish" ni bosadi, bir daqiqadan keyin tizim uni
    o'zi to'xtatadi. Tashqaridan bu "tizim buzuq" bo'lib ko'rinadi va
    operator qayta bosadi — audit jurnalida tebranish shundan paydo
    bo'ladi.

    ⚠️ JOB XATO QILMAYAPTI: paketsiz "faol" holatning ma'nosi yo'q.
    Va BALANSDAGI PUL PAKET EMAS — to'ldirish o'z-o'zidan xizmat
    bermaydi. Xato panelda edi: u buni aytmasdi.

    So'rov `SubscriberGrants` dagisi bilan BIR XIL kalitda — React Query
    uni deduplikatsiya qiladi, ya'ni qo'shimcha so'rov ketmaydi.

    ⚠️ FAQAT `NEW` va `SUSPENDED` da so'raladi. Ogohlantirish shu ikki
    holatdagi tugmaga ("Faollashtirish" / "Davom ettirish") bog'langan va
    boshqa holatlarda o'sha tugmalar UMUMAN chizilmaydi — ya'ni javob
    hech qachon ishlatilmasdi. Amaldagi abonent eng ko'p uchraydigan
    holat, va u kartochkasi ochilganda bu so'rov faqat bekorga ketardi.

    ⚠️ `guard` ning "noma'lum bo'lsa to'xtatma" mantiqi buzilmaydi: so'rov
    o'chirilgan holatlarda uni chaqiradigan tugma ham yo'q.
  */
  const needsGrantCheck = subscriber.state === 'NEW' || subscriber.state === 'SUSPENDED'
  const overview = useQuery({
    queryKey: queryKeys.subscribers.grantOverview(subscriber.id),
    queryFn: () => fetchGrantOverview(subscriber.id),
    enabled: needsGrantCheck,
    staleTime: GRANT_STALE_MS,
  })
  const hasUsableGrant = (overview.data?.usable.length ?? 0) > 0
  const [editOpen, setEditOpen] = useState(false)
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null)

  // Amaldan keyin ikkala kesh ham yangilanadi: kartochka (holat tegi) va
  // ro'yxat (operator orqaga qaytganda eskisini ko'rmasin)
  const runner = useOperationRunner([
    queryKeys.subscribers.detail(subscriber.id),
    queryKeys.subscribers.all,
  ])

  const [passwordForm] = Form.useForm<{ password: string }>()
  const [reasonForm] = Form.useForm<{ reason?: string }>()

  const state = subscriber.state
  const isClosed = state === 'CLOSED'

  const submitReason = () => {
    submitOnValid(reasonForm, async (values) => {
      const reason = values.reason?.trim() || undefined
      const action = reasonAction
      if (!action) return
      const outcome = await runner.run(() => {
        if (action === 'block') return blockSubscriber(subscriber.id, reason)
        if (action === 'suspend') return suspendSubscriber(subscriber.id, reason)
        return closeSubscriber(subscriber.id, reason)
      })
      // FAILED da modal OCHIQ qoladi — sabab matni yo'qolmasin va operator
      // qayta urinishi mumkin bo'lsin
      if (outcome.status !== 'FAILED') {
        setReasonAction(null)
        reasonForm.resetFields()
      }
    })
  }

  /**
   * Paketsiz faollashtirishdan oldin ogohlantiradi.
   *
   * ⚠️ TO'SIQ EMAS, OGOHLANTIRISH: operator paketni shu zahoti sotmoqchi
   * bo'lishi mumkin va ketma-ketlik ("avval faollashtir, keyin sot")
   * qonuniy. Majburiy taqiq ishlaydigan stsenariyni buzardi.
   *
   * ⚠️ Grant ma'lumoti hali yuklanmagan bo'lsa ham TO'XTATILMAYDI:
   * "bilmayman" ni "yo'q" deb hisoblash operatorga yolg'on ogohlantirish
   * ko'rsatardi.
   */
  const guard = (proceed: () => void) => {
    if (hasUsableGrant || overview.isPending) {
      proceed()
      return
    }
    modal.confirm({
      title: t('subscriber.noGrantWarn.title'),
      content: t('subscriber.noGrantWarn.text'),
      okText: t('subscriber.noGrantWarn.ok'),
      cancelText: t('common.cancel'),
      onOk: proceed,
    })
  }

  const submitPassword = () => {
    submitOnValid(passwordForm, async (values) => {
      const outcome = await runner.run(() => changePassword(subscriber.id, values.password))
      if (outcome.status !== 'FAILED') {
        setPasswordOpen(false)
        passwordForm.resetFields()
      }
    })
  }

  return (
    <>
      <Space wrap>
        {/* Yopilgan hisob terminal holatda — uni tahrirlash ma'nosiz va
            backend baribir o'zgarishni RADIUS'ga chiqarmaydi */}
        {!isClosed && (
          <Can permission="subscriber.update">
            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              {t('subscriber.edit.button')}
            </Button>
          </Can>
        )}

        {state === 'NEW' && (
          <Can permission="subscriber.update">
            <Button
              type="primary"
              loading={runner.running}
              onClick={() => guard(() => void runner.run(() => activateSubscriber(subscriber.id)))}
            >
              {t('actions.activate')}
            </Button>
          </Can>
        )}

        {state === 'ACTIVE' && (
          <Can permission="subscriber.update">
            <Button loading={runner.running} onClick={() => setReasonAction('suspend')}>
              {t('actions.suspend')}
            </Button>
          </Can>
        )}

        {state === 'SUSPENDED' && (
          <Can permission="subscriber.update">
            <Button
              type="primary"
              loading={runner.running}
              onClick={() => guard(() => void runner.run(() => resumeSubscriber(subscriber.id)))}
            >
              {t('actions.resume')}
            </Button>
          </Can>
        )}

        {(state === 'ACTIVE' || state === 'SUSPENDED') && (
          <Can permission="subscriber.block">
            <Button danger loading={runner.running} onClick={() => setReasonAction('block')}>
              {t('actions.block')}
            </Button>
          </Can>
        )}

        {state === 'BLOCKED' && (
          <Can permission="subscriber.block">
            <Button
              type="primary"
              loading={runner.running}
              onClick={() => void runner.run(() => unblockSubscriber(subscriber.id))}
            >
              {t('actions.unblock')}
            </Button>
          </Can>
        )}

        {!isClosed && (
          <Can permission="subscriber.password.set">
            <Button onClick={() => setPasswordOpen(true)}>{t('actions.password.button')}</Button>
          </Can>
        )}

        {!isClosed && (
          <Can permission="subscriber.delete">
            <Button danger onClick={() => setReasonAction('close')}>
              {t('actions.close')}
            </Button>
          </Can>
        )}
      </Space>

      {/*
        ⚠️ NATIJA KARTADA FAQAT OYNA YOPIQ BO'LGANDA (tester-2, BUG-006).

        Ikkala oyna ham `FAILED` da ATAYLAB ochiq qoladi (`submitPassword`,
        `submitReason`), natija esa faqat shu yerda chizilardi — ya'ni
        `.ant-modal-mask` ning ORTIDA. Server aniq sabab aytgan holda
        («Holat o'tishi mumkin emas», «Parol juda qisqa») operator
        ekranda HECH NARSA ko'rmasdi va tugmani qayta bosardi.

        `SubscriberFundAction` da bu naqsh allaqachon to'g'rilangan edi,
        lekin AYNI OILA shu faylda ikki marta takrorlangan holda qolgan
        edi.
      */}
      {runner.result && !passwordOpen && reasonAction === null ? (
        <div style={{ marginTop: 16 }}>
          <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
        </div>
      ) : null}

      <EditSubscriberModal
        open={editOpen}
        subscriber={subscriber}
        onClose={() => setEditOpen(false)}
      />

      <Modal
        open={passwordOpen}
        title={t('actions.password.title')}
        // ⚠️ Bekor qilib chiqishda natija TOZALANADI: yiqilgan urinishning
        // xabari keyingi ochilishda begona bo'lardi.
        onCancel={() => {
          runner.clear()
          setPasswordOpen(false)
        }}
        onOk={submitPassword}
        confirmLoading={runner.running}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/* ⚠️ XATO SHU YERDA — parol maydoni bilan BIR EKRANDA. */}
        {runner.result ? (
          <div style={{ marginBottom: 16 }}>
            <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
          </div>
        ) : null}

        {/* Parolni KO'RSATISH imkoniyati ataylab yo'q (CLAUDE.md §7):
            u ochiq saqlanadi (MS-CHAPv2 talabi), lekin panelda o'qish
            endpointi ham, maydoni ham bo'lmasligi kerak */}
        <Form form={passwordForm} layout="vertical" preserve={false}>
          <Form.Item
            name="password"
            label={t('actions.password.newPassword')}
            extra={t('actions.password.effectHint')}
            rules={[
              { required: true, message: t('operation.validation.passwordRequired') },
              { min: 4, max: 128, message: t('operation.validation.passwordLength') },
            ]}
          >
            <Input.Password autoFocus autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={reasonAction !== null}
        title={reasonAction ? t(`actions.${reasonAction}Title`) : ''}
        onCancel={() => {
          runner.clear()
          setReasonAction(null)
        }}
        onOk={submitReason}
        confirmLoading={runner.running}
        okText={t('common.done')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: reasonAction !== 'suspend' }}
        destroyOnHidden
      >
        {/* ⚠️ XATO SHU YERDA — sabab maydoni bilan BIR EKRANDA (BUG-006). */}
        {runner.result ? (
          <div style={{ marginBottom: 16 }}>
            <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
          </div>
        ) : null}

        <Form form={reasonForm} layout="vertical" preserve={false}>
          <Form.Item name="reason" label={t('actions.reason')} extra={t('actions.reasonHint')}>
            <Input.TextArea rows={3} maxLength={200} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
